/**
 * Unfinished levels, kept in this browser (packages/vm/src/worksave.ts has
 * what is kept and how it goes back into the machine).
 *
 * IndexedDB and nothing else. A level's work is binary and can run to half a
 * megabyte, which is the wrong shape for localStorage, and unlike progress it
 * has no spare copies and no server copy: it is a convenience that makes a
 * reload cost nothing, not a record. Losing it costs the learner the level's
 * starting point, which is what every reload cost before this existed.
 *
 * A database of its own rather than a store in the vault's (vault.ts): adding
 * a store means raising the vault's version, and an open blocked by another
 * tab still holding the old version would cost the vault its spare copies for
 * as long as that tab lived.
 *
 * Every function resolves and none throws. Opens and transactions have a
 * deadline: browser storage that stops answering cannot prevent a level from
 * opening fresh.
 */
import type { GitEvidence } from '@engineer-os/engine';
import type { SavedWork } from '@engineer-os/vm';

const DB_NAME = 'engineeros-work';
const DB_VERSION = 1;
const STORE = 'levels';
const STORAGE_TIMEOUT_MS = 3000;

/**
 * How many levels are kept at once, most recently saved first. A learner has
 * one level open at a time and rarely more than a handful unfinished; the
 * ceiling is what stops a year of abandoned attempts growing without bound.
 */
export const MAX_KEPT_LEVELS = 30;

/** One unfinished level, and what the grader's plausibility check needs to carry across a reload. */
export interface KeptLevel {
  missionId: string;
  /** `Mission.seedPrint` when this was taken. Restored only while the level's still matches. */
  seedPrint: string;
  /** The per-learner salt the workspace was built with, or null for a shared level. */
  salt: string | null;
  /** Date.now() when this copy was taken. */
  savedAt: number;
  /**
   * When the level was first opened, on the wall clock. Carried so the time a
   * level took is measured from the real start and not from the reload
   * (runtime.ts, `firstOpened`): a learner who comes back tomorrow and
   * finishes in ten seconds did not solve it in ten seconds.
   */
  openedAt: number;
  /** The seeded repository's fingerprint, taken when the level first opened. */
  before: GitEvidence | null;
  /** `Session.startHead` when the level first opened. */
  startHead: string | null;
  /** Commands run and editor saves made before this copy, which a reload would otherwise zero. */
  commands: number;
  saves: number;
  work: SavedWork;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    let settled = false;
    const finish = (db: IDBDatabase | null) => {
      if (settled) {
        // An open can succeed after its deadline. Nobody will use this
        // connection, and leaving it open could block a later upgrade.
        db?.close();
        return;
      }
      settled = true;
      clearTimeout(timer);
      resolve(db);
    };
    const timer = setTimeout(() => finish(null), STORAGE_TIMEOUT_MS);
    try {
      if (typeof indexedDB === 'undefined') return finish(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'missionId' }).createIndex('savedAt', 'savedAt');
        }
      };
      req.onsuccess = () => finish(req.result);
      req.onerror = () => finish(null);
      // As in vault.ts: Firefox's private window resolves neither.
      req.onblocked = () => finish(null);
    } catch {
      finish(null);
    }
  });
  return dbPromise;
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  return openDb().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null);
        let settled = false;
        let tx: IDBTransaction | undefined;
        const finish = (value: T | null) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(value);
        };
        const fail = () => {
          finish(null);
          // Release locks as well as the caller. A stuck write must not
          // leave every later read queued behind it.
          try { tx?.abort(); } catch { /* already finished */ }
        };
        const timer = setTimeout(fail, STORAGE_TIMEOUT_MS);
        try {
          tx = db.transaction(STORE, mode);
          const req = work(tx.objectStore(STORE));
          // A successful request is not yet a committed transaction. Keep
          // the deadline until completion so a stalled commit is bounded too.
          tx.oncomplete = () => finish(req.result);
          req.onerror = fail;
          tx.onabort = () => finish(null);
          tx.onerror = fail;
        } catch {
          fail();
        }
      }),
  );
}

function valid(value: unknown): value is KeptLevel {
  const k = value as Partial<KeptLevel> | null;
  return (
    !!k &&
    typeof k.missionId === 'string' &&
    typeof k.seedPrint === 'string' &&
    (k.salt === null || typeof k.salt === 'string') &&
    typeof k.savedAt === 'number' &&
    typeof k.openedAt === 'number' &&
    typeof k.commands === 'number' &&
    typeof k.saves === 'number' &&
    !!k.work &&
    k.work.archive instanceof Uint8Array &&
    Array.isArray(k.work.dirs) &&
    Array.isArray(k.work.deleted) &&
    typeof k.work.sha256 === 'string'
  );
}

/** This level's kept work, or null. A record in any other shape is treated as absent. */
export async function readKept(missionId: string): Promise<KeptLevel | null> {
  const found = await run<unknown>('readonly', (s) => s.get(missionId));
  return valid(found) ? found : null;
}

/** Keep this level's work, replacing any earlier copy, then trim to the ceiling. */
export async function keep(level: KeptLevel): Promise<void> {
  await run('readwrite', (s) => s.put(level));
  await trim();
}

export async function forgetKept(missionId: string): Promise<void> {
  await run('readwrite', (s) => s.delete(missionId));
}

/** Drop every kept level. For a course reset (progress.ts, `startNewCourse`). */
export async function forgetAllKept(): Promise<void> {
  await run('readwrite', (s) => s.clear());
}

/** Drop the oldest copies beyond MAX_KEPT_LEVELS. Keys only: the archives are never read to be counted. */
async function trim(): Promise<void> {
  const keys = await run<IDBValidKey[]>('readonly', (s) => s.index('savedAt').getAllKeys());
  if (!keys || keys.length <= MAX_KEPT_LEVELS) return;
  // The index is ascending by savedAt, so the oldest come first.
  for (const key of keys.slice(0, keys.length - MAX_KEPT_LEVELS)) {
    await run('readwrite', (s) => s.delete(key));
  }
}
